/**
 * 出歌流程／播放動作（UI v2 步 7：由 `ChatPage.tsx` 抽出，零行為改動）
 *
 *   `handleSend`（送出，含「歌完成 → 下句開新對話」）／`openStartSong`
 *   `runUnderstand`（理解，免費）／`runWriteLyrics`／`runRender`（出歌，**會花錢**）
 *   `playSong`（全 app 播放器）／`audioUrlForShare`
 */
import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { trackFromSong, tracksFromSongs, songAudioUrl, canPlaySong } from '../../player/playerModel';
import type { useSongFlow } from '../../hooks/useSongFlow';
import type { useLibrary } from '../../hooks/useLibrary';
import type { usePlayer } from '../../player/PlayerContext';
import type { useAuth } from '../../hooks/useAuth';
import type { useChatStream } from '../../hooks/useChatStream';
import type { MustUseItem } from './useMustUse';
import type { ChatMode, SheetKind, SongLang, SongRegister, VocalGender } from './types';

export interface UseSongActionsDeps {
  t: TFunction;
  sessionId: string;
  deviceId: string;
  locale: string;
  flow: ReturnType<typeof useSongFlow>;
  auth: ReturnType<typeof useAuth>;
  library: ReturnType<typeof useLibrary>;
  player: ReturnType<typeof usePlayer>;
  send: ReturnType<typeof useChatStream>['send'];
  pinned: MustUseItem[];
  songLang: SongLang;
  songRegister: SongRegister;
  songGenre: string | null;
  voiceReady: boolean;
  useVoice: boolean;
  renderVariants: 1 | 2;
  vocalGender: VocalGender;
  mode: ChatMode;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  songFinished: boolean;
  keepGoing: boolean;
  setKeepGoing: Dispatch<SetStateAction<boolean>>;
  setSheet: (s: SheetKind) => void;
  setBusyAction: Dispatch<SetStateAction<boolean>>;
  setActionError: Dispatch<SetStateAction<string | null>>;
  setRenderingId: Dispatch<SetStateAction<string | null>>;
  reload: () => void;
}

export function useSongActions(deps: UseSongActionsDeps) {
  const {
    t, sessionId, deviceId, locale, flow, auth, library, player, send, pinned,
    songLang, songRegister, songGenre, voiceReady, useVoice, renderVariants, vocalGender, mode,
    input, setInput, songFinished, keepGoing, setKeepGoing,
    setSheet, setBusyAction, setActionError, setRenderingId, reload,
  } = deps;

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setActionError(null);
    const autoNew = songFinished && !keepGoing;
    setKeepGoing(false);
    void send(text, { newChat: autoNew, mode });
  };

  const openStartSong = () => setSheet('lang');

  /** 理解步驟（免費） */
  const runUnderstand = async () => {
    setSheet('none');
    setBusyAction(true);
    setActionError(null);
    try {
      const res = await fetch('/api/song/understand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, deviceId, locale, songLanguage: songLang, lyricRegister: songRegister }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) setActionError(`${t('song_error_generic')}${data.error ? `（${data.error}）` : ''}`);
      reload();
    } catch (err) {
      setActionError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
    } finally {
      setBusyAction(false);
    }
  };

  const runWriteLyrics = async () => {
    setBusyAction(true);
    await flow.writeLyrics(pinned, songLang, songRegister, songGenre);
    setBusyAction(false);
    reload();
  };

  /** 〔出歌〕（**會花錢**；⭐ 0017：會**扣 1 首額度**，server 側把關） */
  const runRender = async (songId: string) => {
    setActionError(null);
    setBusyAction(true);
    try {
      const res = await fetch(`/api/song/${songId}/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /** ⭐ 0017：`variants` ＝ 今次出 1 個定 2 個版本（產品負責人 2026-09-25 要求由用戶揀） */
        body: JSON.stringify({ sessionId, deviceId, locale, useVoice: voiceReady && useVoice, variants: renderVariants, vocalGender }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; note?: string; status?: string };
      /**
       * ⭐ **2026-09-23 修**：`already_running`／`already_done` **唔係錯誤**（照實講人話）：
       *   · `insufficient_credits`（402）→ 去「我的」買套餐；`login_required`（401）→ 重新登入
       */
      const alreadyRunning = data.note === 'already_running' || (data.error === 'not_ready_for_render' && data.status === 'running');
      if (alreadyRunning) {
        setRenderingId(songId);
      } else if (data.note === 'already_done') {
        setRenderingId(null);
        reload();
      } else if (data.error === 'insufficient_credits') {
        setActionError(t('song_error_insufficient_credits'));
        auth.refresh();
      } else if (data.error === 'login_required') {
        setActionError(t('song_error_login_required'));
      } else if (!res.ok) {
        setActionError(`${t('song_error_generic')}${data.error ? `（${data.error}）` : ''}`);
      } else {
        setRenderingId(songId);
        /** ⭐ 0017：出歌會扣額度 → 即刻更新「剩幾首」顯示 */
        auth.refresh();
      }
    } catch (err) {
      setActionError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
    } finally {
      setBusyAction(false);
    }
  };

  /** ▶ 播放（全 app 播放器；清單 ＝ 個人 library）—— 未有音頻要**照實講**（唔准靜靜失敗） */
  const playSong = (songId: string) => {
    const owner = { deviceId };
    const src = library.songs.find((s) => s.id === songId);
    if (!canPlaySong(src)) {
      setActionError(t('song_audio_not_ready'));
      reload();
      return;
    }
    const track = trackFromSong(src!, owner);
    if (!track) {
      setActionError(t('song_audio_not_ready'));
      return;
    }
    player.play(track, tracksFromSongs(library.songs, owner));
  };

  /** 音頻網址（**分享**用）：跟住「揀住嗰個版本」嘅 mp3；砌法只此一處（`playerModel.songAudioUrl`） */
  const audioUrlForShare = (songId: string): string => {
    const src = library.songs.find((s) => s.id === songId);
    return songAudioUrl(songId, { deviceId }, { variant: src?.pickedIndex ?? 0 });
  };

  return { handleSend, openStartSong, runUnderstand, runWriteLyrics, runRender, playSong, audioUrlForShare };
}

